import type { StateTree } from 'pinia'

const isStateTree = (state: unknown): state is StateTree => {
  return typeof state === 'object' && state !== null
}

export default defineNuxtPlugin({
  name: 'sentry',
  dependsOn: ['pinia', 'unleash'],
  async setup(nuxtApp) {
    const {
      public: { sentry, domainName, mode, isTest },
    } = useRuntimeConfig()

    const isDisabled = mode === 'development' || isTest

    if (!sentry.dsn || isDisabled) {
      return {}
    }

    const router = useRouter()
    const { $pinia, $unleash } = useNuxtApp()
    const { platform } = storeToRefs(useSystemStore())

    const Sentry = await import('@sentry/vue')

    const environment = sentry.config.environment || (platform.value === 'EU' ? `de-${mode}` : mode)

    Sentry.init({
      app: nuxtApp.vueApp,
      dsn: sentry.dsn,
      environment,
      release: sentry.config.release,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        Sentry.unleashIntegration({ featureFlagClientClass: $unleash.baseType }),
      ],
      beforeSend(event, hint) {
        const error = hint?.originalException
        // Drop expected initial unauth gateway errors to reduce noise
        if (isFetchError(error) && isGatewayUnauthResponse(error.response)) return null
        // Drop revoked-session errors, the HTTP layer logs the user out
        if (isFetchError(error) && isGatewaySessionRevokedResponse(error.response)) return null
        // Drop abort errors to reduce noise
        if (isAbortError(error)) return null
        return event
      },
      tracePropagationTargets: ['localhost', `https://my.${domainName}`],
      tracesSampleRate: mode === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: mode === 'production' ? 0.01 : 1.0,
      replaysOnErrorSampleRate: 1.0,
    })

    $pinia.use(
      Sentry.createSentryPiniaPlugin({
        stateTransformer(state) {
          // We only want to send selected parts of the state
          const transformedState: Record<string, StateTree> = {}

          if (isStateTree(state.user)) {
            const { role } = state.user
            transformedState.user = { role }
          }

          if (isStateTree(state.business)) {
            const { id, pricePlan } = state.business
            transformedState.business = { id, pricePlan }
          }

          return transformedState
        },
      })
    )

    if ('LEGACY' in import.meta.env) {
      Sentry.setTag('build_type', import.meta.env.LEGACY ? 'legacy' : 'modern')
    }

    return {
      provide: {
        sentry: {
          captureException: Sentry.captureException,
          captureMessage: Sentry.captureMessage,
          captureEvent: Sentry.captureEvent,
          setUser: Sentry.setUser,
          setTag: Sentry.setTag,
        },
      },
    }
  },
})
